import React, { useState } from 'react';
import { BatchJob, BatchJobType, STATUS_ICONS, STATUS_COLORS, STATUS_NAMES } from '../../../types/batch';
import { Card, CardContent, CardHeader, CardTitle } from '../../ui/card';
import { Button } from '../../ui/button';

interface Props {
  job: BatchJob;
  onExecute: (jobType: BatchJobType) => Promise<boolean>;
  onViewHistory: (jobType: BatchJobType) => void;
}

export const BatchJobCard: React.FC<Props> = ({ job, onExecute, onViewHistory }) => {
  const [isExecuting, setIsExecuting] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);

  const handleExecute = async () => {
    setShowConfirm(false);
    setIsExecuting(true);
    await onExecute(job.jobType);
    setIsExecuting(false);
  };

  const statusColor = STATUS_COLORS[job.lastExecutionStatus];
  const statusIcon = STATUS_ICONS[job.lastExecutionStatus];
  const statusName = STATUS_NAMES[job.lastExecutionStatus];

  const getStatusBadgeClass = (color: string) => {
    const colorMap: Record<string, string> = {
      green: 'bg-green-100 text-green-800 border-green-300',
      red: 'bg-red-100 text-red-800 border-red-300',
      blue: 'bg-blue-100 text-blue-800 border-blue-300',
      orange: 'bg-orange-100 text-orange-800 border-orange-300',
      gray: 'bg-gray-100 text-gray-800 border-gray-300'
    };
    return colorMap[color] || colorMap.gray;
  };

  return (
    <>
      <Card className="hover:shadow-lg transition-shadow">
        <CardHeader>
          <div className="flex justify-between items-start">
            <div>
              <CardTitle className="text-lg">{job.displayName}</CardTitle>
              <div className="mt-2 text-sm text-gray-600 flex items-center gap-2">
                <span>⏰</span>
                <span>{job.scheduleDescription}</span>
              </div>
            </div>
            <div className={`px-3 py-1 rounded-full text-sm font-medium border ${getStatusBadgeClass(statusColor)}`}>
              <span className="mr-1">{statusIcon}</span>
              {statusName}
            </div>
          </div>
        </CardHeader>
        <CardContent>
          <div className="space-y-3">
            {/* 최근 실행 정보 */}
            <div>
              <div className="text-sm text-gray-600">최근 실행</div>
              <div className="text-base font-medium text-gray-900">
                {job.lastExecutionTime 
                  ? new Date(job.lastExecutionTime).toLocaleString('ko-KR', {
                      year: 'numeric',
                      month: '2-digit',
                      day: '2-digit',
                      hour: '2-digit',
                      minute: '2-digit',
                      second: '2-digit'
                    })
                  : '아직 실행된 적 없음'}
              </div>
              {job.lastExecutionResult && (
                <div className="text-sm text-gray-600 mt-1">
                  결과: {job.lastExecutionResult}
                </div>
              )}
            </div>

            {/* 통계 */}
            <div className="grid grid-cols-2 gap-3 pt-3 border-t">
              <div>
                <div className="text-xs text-gray-500">성공률 (30일)</div>
                <div className="text-lg font-bold text-gray-900">
                  {job.successRate30Days !== null ? `${job.successRate30Days.toFixed(1)}%` : '-'}
                </div>
              </div>
              <div>
                <div className="text-xs text-gray-500">평균 시간</div>
                <div className="text-lg font-bold text-gray-900">
                  {job.avgDuration30Days !== null 
                    ? (job.avgDuration30Days < 1000 
                      ? `${job.avgDuration30Days}ms`
                      : `${(job.avgDuration30Days / 1000).toFixed(1)}초`)
                    : '-'}
                </div>
              </div>
            </div>

            {/* 버튼 */}
            <div className="flex gap-2 pt-3">
              <Button
                variant="outline"
                onClick={() => onViewHistory(job.jobType)}
                className="flex-1"
              >
                📜 실행 이력
              </Button>
              <Button
                onClick={() => setShowConfirm(true)}
                disabled={isExecuting}
                className="flex-1"
              >
                {isExecuting ? '실행 중...' : '▶️ 수동 실행'}
              </Button>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* 실행 확인 모달 */}
      {showConfirm && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50" onClick={() => setShowConfirm(false)}>
          <div className="bg-white rounded-lg max-w-md w-full mx-4" onClick={e => e.stopPropagation()}>
            <div className="p-6">
              <h3 className="text-lg font-bold mb-4">배치 작업 수동 실행</h3>
              <p className="text-gray-700 mb-4">
                "<span className="font-bold">{job.displayName}</span>" 배치를 즉시 실행하시겠습니까?
              </p>
              <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4 mb-4">
                <div className="flex items-start gap-2">
                  <span className="text-yellow-600 text-xl">⚠️</span>
                  <div className="text-sm text-yellow-800">
                    <div className="font-medium mb-1">주의사항</div>
                    <ul className="list-disc list-inside space-y-1">
                      <li>현재 실행 중인 동일 배치가 있다면 <strong>중복 실행이 방지</strong>됩니다</li>
                      <li>배치 실행 중에는 시스템 부하가 증가할 수 있습니다</li>
                      <li>실행 결과는 "📜 실행 이력"에서 확인할 수 있습니다</li>
                    </ul>
                  </div>
                </div>
              </div>
              <div className="flex gap-2 justify-end">
                <Button
                  variant="outline"
                  onClick={() => setShowConfirm(false)}
                >
                  취소
                </Button>
                <Button
                  onClick={handleExecute}
                >
                  확인 및 실행
                </Button>
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
};
